/**
 * surface-reflow — math-триггер «навигация вбок» для списочных surface'ов (лидерборд,
 * Архив реплеев). owner-decision 2026-06-25 (личная доработка с ownerّом).
 *
 * ПРОБЛЕМА: на КОРОТКОМ ландшафте (телефон landscape, напр. realme 724×246) в стековой
 * раскладке (обвязка сверху: заголовок / переключатели / вкладки, список под ними) под
 * список остаётся 1-3 строки — экран бестолковый. Старый side-rail (narrow-side-rail.css)
 * лечил это «тип устройства + landscape» без математики → коллизии и «уезжало вниз»
 * (grid-blowout) → owner отверг. ПРАВИЛЬНО: переключаем раскладку ТОЛЬКО когда
 * математически в стеке влезает < N строк, а перенос — чистый remap grid-зон (CSS):
 * обвязка уезжает в ПРАВУЮ колонку, список — левая колонка на всю высоту. Зоны
 * взаимоисключающие → коллизий нет by construction (W3C C32); список-колонка =
 * minmax(0,1fr) + scroll{min-height:0} → нет grid-blowout (CSS-Tricks).
 *
 * ОБЩИЙ модуль (НЕ дублируем по экранам, CLAUDE.md «ствол → ветки»): и лидерборд, и
 * Архив используют ОДНУ математику; различаются лишь числом nav-блоков обвязки
 * (navRows) и служебной высотой списка (listOverheadM). Экран-ветка вызывает
 * installSurfaceReflow со своими параметрами; CSS-ветка маппит grid-зоны под
 * `[data-reflow='on']`.
 *
 * ПОЧЕМУ ФОРМУЛА, А НЕ ИЗМЕРЕНИЕ ТЕКУЩЕЙ РАСКЛАДКИ: решение считается для
 * ГИПОТЕТИЧЕСКОГО стека ВСЕГДА (вне зависимости от текущей раскладки). Иначе осцилляция:
 * стек→мало строк→reflow→список во всю высоту→много строк→назад в стек→… Формула от
 * измеренного вьюпорта (как viewport-sizer считает доску) детерминирована и стабильна.
 * Device-agnostic (owner-rule «только формула, без дискретных разрешений»).
 */

import {
  VIEWPORT_APPLIED_EVENT,
  computeGeometry,
  type ViewportGeometry,
} from './viewport-sizer.js';

/** Порог строк по умолчанию (owner-decision 2026-06-25: N = 7). */
export const DEFAULT_MIN_ROWS = 7;

export interface SurfaceReflowOptions {
  /** Число nav-блоков обвязки в стеке (каждый ≈ контрол-высота --btn-h). Лидерборд = 3
   *  (header/top/sub), Архив = 5 (header/album/count/tabs/expand). */
  readonly navRows: number;
  /** Доп. служебная высота списка в единицах δ. Таблица-лидерборд: строка-заголовок
   *  колонок (~3m) + scroll padding (~1m) ≈ 4. Список Архива: заголовка нет ≈ 1. */
  readonly listOverheadM?: number;
  /** Порог строк, ниже которого переносим обвязку вбок (default 7). */
  readonly minRows?: number;
}

/**
 * Сколько строк влезает в СТЕКОВОЙ раскладке. Все коэффициенты трассируются токенами
 * (§0.G): экран-padding 2m; обвязка navRows·btnH + navRows межзонных gap'ов (grid gap=m);
 * служебное списка listOverheadM·m; строка = btnH + row-gap 0.25m.
 */
export function stackedRowsFit(g: ViewportGeometry, navRows: number, listOverheadM = 4): number {
  const m = g.m;
  const btnH = g.tokens.btnH;
  const rowGap = 0.25 * m;
  const available = g.dvh - 2 * m - navRows * btnH - navRows * m - listOverheadM * m;
  const rowFootprint = btnH + rowGap;
  if (rowFootprint <= 0) return 0;
  return Math.max(0, Math.floor((available + rowGap) / rowFootprint));
}

/**
 * Нужен ли перенос обвязки вбок. ДВА условия: (1) есть гориз. место — только rail-layout
 * (aspect ≥ 0.8; в портрете колонке навигации негде встать → всегда стек); (2) в стеке
 * влезает < minRows строк.
 */
export function shouldReflow(g: ViewportGeometry, opts: SurfaceReflowOptions): boolean {
  if (g.layout !== 'rail') return false;
  return stackedRowsFit(g, opts.navRows, opts.listOverheadM) < (opts.minRows ?? DEFAULT_MIN_ROWS);
}

/** Измерить геометрию из живого documentElement (как applyCore viewport-sizer'а). */
function measure(): ViewportGeometry | null {
  if (typeof document === 'undefined') return null;
  const el = document.documentElement;
  const ch = el.clientHeight;
  const vv = typeof window !== 'undefined' ? window.visualViewport : null;
  // Живая видимая высота (адресная строка / клавиатура) — как в viewport-sizer; при
  // pinch-zoom (scale≠1) падаем на layout-высоту, чтобы жест не дёргал решение.
  const liveDvh = vv && vv.scale === 1 && vv.height > 0 ? vv.height : ch;
  return computeGeometry(el.clientWidth, ch, liveDvh);
}

/**
 * Навесить на корень экрана атрибут `data-reflow` (on/off) и держать его актуальным при
 * ресайзе/повороте через сигнал viewport-sizer'а 'lines98:viewport-applied' (он уже
 * коалесцирован rAF + dirty-check → ресурс-эффективно). Возвращает teardown — снять
 * слушатель (вызвать в unmount экрана).
 */
export function installSurfaceReflow(root: HTMLElement, opts: SurfaceReflowOptions): () => void {
  const update = (): void => {
    const g = measure();
    root.dataset.reflow = g && shouldReflow(g, opts) ? 'on' : 'off';
  };
  update();
  if (typeof window !== 'undefined') {
    window.addEventListener(VIEWPORT_APPLIED_EVENT, update);
  }
  return () => {
    if (typeof window !== 'undefined') {
      window.removeEventListener(VIEWPORT_APPLIED_EVENT, update);
    }
  };
}
